import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './TickerSearch.css';

export default function TickerSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchResults = useCallback(async (q) => {
    if (!q || q.length < 1) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`/api/market/search?q=${encodeURIComponent(q)}&limit=8`);
      if (resp.ok) {
        const data = await resp.json();
        setResults(data);
        setOpen(data.length > 0);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setSelectedIdx(-1);

    // Debounce API calls
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(val), 250);
  };

  const handleSelect = (symbol, assetType) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    // Navigate to screener with the selected symbol. When the user picked a
    // specific result (equity vs crypto), carry that choice as a hint so the
    // backend honors it instead of re-guessing from the bare ticker.
    const params = new URLSearchParams({ symbols: symbol });
    if (assetType) params.set('hints', `${symbol}:${assetType}`);
    navigate(`/screener?${params.toString()}`);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) {
      if (e.key === 'Enter' && query.trim()) {
        e.preventDefault();
        handleSelect(query.trim().toUpperCase());
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIdx >= 0 && results[selectedIdx]) {
        handleSelect(results[selectedIdx].symbol, results[selectedIdx].asset_type);
      } else if (query.trim()) {
        handleSelect(query.trim().toUpperCase());
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="ticker-search" ref={wrapperRef}>
      <div className="ticker-search-input-wrapper">
        <svg className="ticker-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          ref={inputRef}
          className="ticker-search-input"
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder="Search ticker..."
          spellCheck={false}
          autoComplete="off"
        />
        {loading && <span className="ticker-search-spinner" />}
      </div>

      {open && results.length > 0 && (
        <div className="ticker-search-dropdown">
          {results.map((r, i) => (
            <button
              key={`${r.symbol}-${r.asset_type || 'security'}`}
              className={`ticker-search-result ${i === selectedIdx ? 'selected' : ''}`}
              onClick={() => handleSelect(r.symbol, r.asset_type)}
              onMouseEnter={() => setSelectedIdx(i)}
            >
              <span className="ticker-result-symbol">{r.symbol}</span>
              <span className="ticker-result-name">{r.name}</span>
              {r.type && (
                <span className={`ticker-result-type ${r.asset_type === 'crypto' ? 'crypto' : ''}`}>{r.type}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
